import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  Image,
  Modal,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";

import {
  Icon,
  IconProps,
  type IconSet,
  useCustomTheme,
} from "@luna-restaurant/ui";

const chefHatImg = require("@/assets/images/chef-hat.png");

const SMALL_ICON_SIZE = 44;
const LARGE_ICON_SIZE = 50;
const SMALL_ICON_HORIZONTAL_RATIO = 0.4;
const LARGE_ICON_HORIZONTAL_RATIO = 0.22;
const SMALL_ICON_VERTICAL_RATIO = 0.55;
const LARGE_ICON_VERTICAL_RATIO = 0.42;
const SHEET_SIDE_MARGIN = 24;
const DEFAULT_SHEET_MAX_WIDTH = 1_366;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const interpolateRatio = (
  iconSize: number,
  minSize: number,
  maxSize: number,
  minRatio: number,
  maxRatio: number,
) => {
  if (minSize === maxSize) return minRatio;

  const factor = clamp((iconSize - minSize) / (maxSize - minSize), 0, 1);
  return minRatio + (maxRatio - minRatio) * factor;
};

type ChefHatModalBaseProps = {
  visible: boolean;
  onDismiss: () => void;
  children: ReactNode;
  backdropTestID?: string;
  iconSize: IconProps["size"];
  icon: IconSet;
};

type ChefHatModalWidthProps =
  | {
      fullWidth: true;
      maxWidth?: never;
    }
  | {
      fullWidth?: false;
      maxWidth?: number;
    };

type ChefHatModalProps = ChefHatModalBaseProps & ChefHatModalWidthProps;

export function ChefHatModal({
  visible,
  icon,
  iconSize = 48,
  onDismiss,
  maxWidth,
  fullWidth = false,
  children,
  backdropTestID = "chef-hat-modal-backdrop",
}: ChefHatModalProps) {
  const { colors } = useCustomTheme();
  const screenDimensions = useWindowDimensions();
  const [modalVisible, setModalVisible] = useState(visible);
  const animation = useRef(new Animated.Value(visible ? 1 : 0)).current;

  const iconOutlineSize = iconSize + 10;
  const horizontalOffsetRatio = interpolateRatio(
    iconSize,
    SMALL_ICON_SIZE,
    LARGE_ICON_SIZE,
    SMALL_ICON_HORIZONTAL_RATIO,
    LARGE_ICON_HORIZONTAL_RATIO,
  );
  const verticalOffsetRatio = interpolateRatio(
    iconSize,
    SMALL_ICON_SIZE,
    LARGE_ICON_SIZE,
    SMALL_ICON_VERTICAL_RATIO,
    LARGE_ICON_VERTICAL_RATIO,
  );

  useEffect(() => {
    animation.stopAnimation();

    if (visible) {
      if (!modalVisible) {
        setModalVisible(true);
      }
      Animated.timing(animation, {
        toValue: 1,
        duration: 240,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
      return;
    }

    Animated.timing(animation, {
      toValue: 0,
      duration: 200,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        setModalVisible(false);
      }
    });
  }, [animation, modalVisible, visible]);

  const handleDismiss = () => {
    if (!visible) return;
    onDismiss();
  };

  if (!modalVisible) {
    return null;
  }

  const pointerEvents = modalVisible ? "auto" : "none";
  const overlayOpacity = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const sheetTranslateY = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [screenDimensions.height, 0],
  });

  const availableWidth = Math.max(
    0,
    screenDimensions.width - SHEET_SIDE_MARGIN * 2,
  );
  const effectiveMaxWidth =
    maxWidth ?? (fullWidth ? 2000 : DEFAULT_SHEET_MAX_WIDTH);
  const sheetWidth = Math.min(availableWidth, effectiveMaxWidth);

  return (
    <Modal
      animationType="none"
      transparent
      visible={modalVisible}
      presentationStyle="overFullScreen"
      onRequestClose={handleDismiss}
    >
      <View style={styles.container}>
        <Pressable
          onPress={handleDismiss}
          style={[styles.backdropPressable, { pointerEvents }]}
        >
          <Animated.View
            style={[
              styles.backdrop,
              {
                opacity: overlayOpacity,
              },
            ]}
            testID={backdropTestID}
          />
        </Pressable>

        <Animated.View
          style={[
            styles.sheet,
            {
              backgroundColor: colors.background,
              shadowColor: colors.text,
              transform: [{ translateY: sheetTranslateY }],
              width: sheetWidth,
              maxWidth,
            },
          ]}
        >
          <View style={styles.sheetInner}>
            <View
              style={[
                styles.mediaColumn,
                { backgroundColor: colors.successLighter },
              ]}
            >
              <View style={styles.mediaContainer}>
                <Image
                  source={chefHatImg}
                  style={styles.mediaImage}
                  resizeMode="contain"
                />
                <View
                  style={[
                    styles.mediaIcon,
                    {
                      right: -iconOutlineSize * horizontalOffsetRatio,
                      bottom: -iconOutlineSize * verticalOffsetRatio,
                      width: iconOutlineSize,
                      height: iconOutlineSize,
                    },
                  ]}
                >
                  <Icon
                    {...icon}
                    size={iconOutlineSize}
                    color={colors.successLighter}
                    cssStyle={styles.mediaIconOutline}
                  />
                  <Icon
                    {...icon}
                    size={iconSize}
                    color={colors.border}
                    cssStyle={styles.mediaIconForeground}
                  />
                </View>
              </View>
            </View>

            <View style={styles.content}>{children}</View>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(14, 18, 23, 0.35)",
  },
  backdropPressable: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    borderRadius: 28,
    elevation: 16,
    shadowOpacity: 0.2,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: -6 },
    marginHorizontal: SHEET_SIDE_MARGIN,
  },
  sheetInner: {
    flexDirection: "row",
    gap: 24,
    alignItems: "stretch",
  },
  mediaColumn: {
    width: 208,
    borderTopLeftRadius: 28,
    borderBottomLeftRadius: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  mediaContainer: {
    position: "relative",
  },
  mediaImage: {
    width: 70,
    height: 70,
  },
  mediaIconForeground: {
    position: "absolute",
  },
  mediaIcon: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  mediaIconOutline: {
    position: "absolute",
  },
  content: {
    paddingVertical: 40,
    paddingHorizontal: 40,
    flex: 1,
  },
});
